// signalingRequest opens a short-lived WebSocket to the runtime's signaling
// endpoint, sends one request, and resolves with the matching "<type>Result"
// message. Both runtimes accept command messages on a bare connection (no
// WebRTC session needed), so callers do not need a persistent
// connection.
export async function signalingRequest(
	wsUrl: string,
	type: string,
	fields: Record<string, unknown>,
	timeoutMs = 15_000,
	responseMatch?: { type: string; key: string; value: string },
	// biome-ignore lint/suspicious/noExplicitAny: shape differs per runtime.
): Promise<any> {
	const id = Math.random().toString(36).slice(2);
	return new Promise((resolve, reject) => {
		const ws = new WebSocket(wsUrl);
		const timer = setTimeout(() => {
			ws.close();
			reject(new Error(`${type} timed out`));
		}, timeoutMs);
		const done = (fn: () => void) => {
			clearTimeout(timer);
			ws.onclose = null;
			ws.close();
			fn();
		};
		ws.onopen = () => ws.send(JSON.stringify({ type, id, ...fields }));
		ws.onerror = () => done(() => reject(new Error("signaling connection failed")));
		ws.onclose = () => done(() => reject(new Error("signaling connection closed")));
		ws.onmessage = (e) => {
			try {
				const msg = JSON.parse(e.data as string);
				if (responseMatch) {
					if (msg.type !== responseMatch.type || msg[responseMatch.key] !== responseMatch.value) return;
				} else if (msg.type !== `${type}Result` || msg.id !== id) return;
				if ((responseMatch && msg.result !== "success") || msg.result === "failure") {
					done(() => reject(new Error(msg.message || "request failed")));
					return;
				}
				if (msg.error) {
					const detail =
						typeof msg.error === "string" ? msg.error : (msg.error.message ?? "request failed");
					done(() => reject(new Error(detail)));
					return;
				}
				done(() => resolve(msg));
			} catch {
				// Ignore frames that are not JSON (e.g. binary keepalives).
			}
		};
	});
}
